<template>
  <div class="cdoc" :class="{ 'is-view': mode === 'view' }">
    <header class="cdoc__head">
      <div>
        <strong>{{ title }}</strong>
        <em v-if="version">v{{ version }}<template v-if="effective"> · 시행 {{ effective }}</template></em>
      </div>
      <div class="cdoc__head-actions">
        <button type="button" class="sui-btn sui-btn--sm" @click="downloadTxt">
          <AppIcon icon="mdi-download" :size="16" />
          텍스트
        </button>
        <button
          type="button"
          class="sui-btn sui-btn--sm sui-btn--primary"
          :disabled="pdfBusy"
          @click="downloadPdf"
        >
          <AppIcon icon="mdi-file-pdf-box" :size="16" />
          {{
            pdfBusy
              ? hasStoredPdf
                ? 'PDF 받는 중…'
                : 'PDF 만드는 중…'
              : hasStoredPdf
                ? '체결 PDF 저장'
                : 'PDF로 저장'
          }}
        </button>
      </div>
    </header>

    <p v-if="introSubtitle" class="cdoc__lead">{{ introSubtitle }}</p>

    <div class="cdoc__toc">
      <button
        v-for="group in contentGroups"
        :key="`toc-${group.id}`"
        type="button"
        class="cdoc__toc-item"
        :class="{ 'is-done': mode === 'view' || checked[group.id] || !group.requiresCheck }"
        @click="scrollTo(group.id)"
      >
        {{ shortTitle(group.title) }}
      </button>
    </div>

    <section
      v-for="group in contentGroups"
      :id="`cdoc-${group.id}`"
      :key="group.id"
      class="cdoc__group"
    >
      <header class="cdoc__group-head">
        <h3>{{ group.title }}</h3>
        <span v-if="group.requiresCheck && mode === 'agree'" class="cdoc__badge" :class="{ 'is-on': checked[group.id] }">
          {{ checked[group.id] ? '확인함' : '확인 필요' }}
        </span>
      </header>

      <template v-if="group.kind === 'basic'">
        <details
          v-for="block in group.blocks"
          :key="block.id"
          class="cdoc__article"
          open
        >
          <summary>{{ block.title }}</summary>
          <ContractBodySegments :body="block.body" />
        </details>
      </template>

      <template v-else-if="group.kind === 'party'">
        <div class="cdoc__party">
          <dl class="cdoc__party-list">
            <div class="cdoc__party-row">
              <dt>갑</dt>
              <dd>주식회사 함우리 / 대표자 김승식 / 사업자등록번호 185-81-02287</dd>
            </div>
            <div class="cdoc__party-row">
              <dt>갑 주소</dt>
              <dd>전북특별자치도 전주시 덕진구 기지로 77, 202동 2803호</dd>
            </div>
            <div class="cdoc__party-row">
              <dt>을</dt>
              <dd>
                <template v-if="partyLine">
                  {{ partyLine }}
                  <template v-if="partyAddress"> / {{ partyAddress }}</template>
                </template>
                <span v-else class="is-muted">[상호 / 대표자 / 사업자등록번호 / 주소]</span>
              </dd>
            </div>
            <div class="cdoc__party-row">
              <dt>계약방식</dt>
              <dd class="cdoc__method-boxes">
                <label class="cdoc__box">
                  <input
                    type="checkbox"
                    :checked="writtenMethod"
                    disabled
                  />
                  <span>서면계약</span>
                </label>
                <label id="cdoc-electronic" class="cdoc__box" :class="{ 'is-pick': mode === 'agree' }">
                  <input
                    v-model="electronicMethod"
                    type="checkbox"
                    :disabled="mode === 'view'"
                  />
                  <span>카이온 전자약관·전자계약</span>
                </label>
              </dd>
            </div>
            <p v-if="mode === 'agree'" class="cdoc__hint">
              온라인 가입은 「카이온 전자약관·전자계약」에 체크해야 합니다. (서면계약은 이 화면에서 선택하지
              않습니다.)
            </p>
            <div class="cdoc__party-row">
              <dt>계약 버전</dt>
              <dd>{{ version || '[시스템 자동표시]' }}</dd>
            </div>
            <div class="cdoc__party-row">
              <dt>체결일·동의자</dt>
              <dd>{{ signedMeta || '[시스템 자동기록 / 성명·직책·본인확인]' }}</dd>
            </div>
          </dl>
          <div v-if="signature" class="cdoc__sig">
            <span>서명</span>
            <img :src="signature" alt="서명" />
          </div>
        </div>
      </template>

      <template v-else>
        <div v-for="block in group.blocks" :key="block.id" class="cdoc__annex">
          <ContractBodySegments :body="block.body" />
        </div>
      </template>

      <label
        v-if="group.requiresCheck && mode === 'agree' && group.kind !== 'party'"
        class="cdoc__check cdoc__check--section"
      >
        <input v-model="checked[group.id]" type="checkbox" />
        {{ group.kind === 'basic' ? '기본계약(제1조~제16조) 내용을 확인했습니다' : `${shortTitle(group.title)} 내용을 확인했습니다` }}
      </label>
      <label
        v-else-if="group.requiresCheck && mode === 'agree' && group.kind === 'party'"
        class="cdoc__check cdoc__check--section"
      >
        <input v-model="checked[group.id]" type="checkbox" />
        계약당사자 정보와 전자계약 방식을 확인했습니다
      </label>
    </section>

    <label v-if="mode === 'agree'" id="cdoc-final" class="cdoc__check cdoc__check--final">
      <input v-model="finalAgree" type="checkbox" />
      공동구매 상품 공급 및 판매운영 기본계약서와 별첨 1~5의 전체 내용을 확인하였고, 계약체결
      권한을 가지고 이에 동의합니다.
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, reactive, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  parseContractBodySegments,
  parseContractSections,
  type ContractBodySegment,
} from '@/utils/parseContractSections'
import { downloadLegalText } from '@/utils/legalAcceptancePreview'
import { downloadContractPdf } from '@/utils/contractPdf'

const ContractBodySegments = defineComponent({
  name: 'ContractBodySegments',
  props: {
    body: { type: String, default: '' },
  },
  setup(props) {
    return () => {
      const segments = parseContractBodySegments(props.body)
      if (!segments.length) {
        return h('div', { class: 'cdoc__body cdoc__body--block' }, '(내용 없음)')
      }
      return h(
        'div',
        { class: 'cdoc__segments' },
        segments.map((seg, index) => renderSegment(seg, index)),
      )
    }
  },
})

function renderSegment(seg: ContractBodySegment, index: number) {
  if (seg.type === 'prose') {
    return h('div', { key: `p-${index}`, class: 'cdoc__body cdoc__body--block' }, seg.text)
  }
  return h('div', { key: `t-${index}`, class: 'cdoc__table-wrap' }, [
    h('table', { class: 'cdoc__table' }, [
      h(
        'thead',
        h(
          'tr',
          seg.headers.map((cell) => h('th', cell)),
        ),
      ),
      h(
        'tbody',
        seg.rows.map((row, ri) =>
          h(
            'tr',
            { key: ri },
            row.map((cell) => h('td', cell || '\u00a0')),
          ),
        ),
      ),
    ]),
  ])
}

const props = withDefaults(
  defineProps<{
    title: string
    version?: string
    effective?: string
    body: string
    mode?: 'agree' | 'view'
    party?: {
      storeName?: string
      ownerName?: string
      bizNumber?: string
      address?: string
      addressDetail?: string
    }
    signature?: string
    signedAt?: string
    signerName?: string
    /** 체결 시 서버에 저장된 PDF를 받을 때 사용 */
    downloadStoredPdf?: () => Promise<void>
  }>(),
  {
    mode: 'view',
    version: '',
    effective: '',
    signature: '',
    signedAt: '',
    signerName: '',
  },
)

const hasStoredPdf = computed(() => typeof props.downloadStoredPdf === 'function')

export type ContractCheckProgress = {
  key: string
  label: string
  done: boolean
  target: string
}

const emit = defineEmits<{
  'update:ready': [value: boolean]
  'update:checks': [items: ContractCheckProgress[]]
}>()

const groups = computed(() => parseContractSections(props.body))
const contentGroups = computed(() => groups.value.filter((g) => g.kind !== 'intro'))
const introSubtitle = computed(() => {
  const intro = groups.value.find((g) => g.kind === 'intro')
  return intro?.blocks[0]?.body || ''
})

const checked = reactive<Record<string, boolean>>({})
/** 온라인 온보딩은 전자계약만 사용. 서면은 표시만 하고 선택 불가 */
const writtenMethod = ref(false)
const electronicMethod = ref(props.mode === 'view')
const finalAgree = ref(false)
const pdfBusy = ref(false)

watch(
  groups,
  (next) => {
    next.forEach((group) => {
      if (group.requiresCheck && checked[group.id] === undefined) {
        checked[group.id] = props.mode === 'view'
      }
    })
    if (props.mode === 'view') {
      electronicMethod.value = true
      finalAgree.value = true
    }
  },
  { immediate: true },
)

const partyLine = computed(() => {
  const p = props.party
  if (!p) return ''
  const parts = [p.storeName, p.ownerName, p.bizNumber].filter(Boolean)
  return parts.join(' / ')
})

const partyAddress = computed(() => {
  const p = props.party
  if (!p?.address) return ''
  return [p.address, p.addressDetail].filter(Boolean).join(' ')
})

const signedMeta = computed(() => {
  if (!props.signedAt && !props.signerName) return ''
  return [props.signedAt, props.signerName].filter(Boolean).join(' · ')
})

const ready = computed(() => {
  if (props.mode === 'view') return true
  if (!electronicMethod.value || !finalAgree.value) return false
  return groups.value
    .filter((g) => g.requiresCheck)
    .every((g) => checked[g.id])
})

function checkLabel(group: { id: string; title: string; kind: string }) {
  if (group.kind === 'basic') return '기본계약 확인'
  if (group.kind === 'party') return '계약당사자 확인'
  const annex = group.title.match(/^별첨\s*\d+/)
  if (annex) return `${annex[0]} 확인`
  return `${shortTitle(group.title)} 확인`
}

const checkProgress = computed<ContractCheckProgress[]>(() => {
  if (props.mode === 'view') return []
  const items: ContractCheckProgress[] = groups.value
    .filter((g) => g.requiresCheck)
    .map((g) => ({
      key: g.id,
      label: checkLabel(g),
      done: Boolean(checked[g.id]),
      target: `cdoc-${g.id}`,
    }))
  items.push({
    key: 'electronic',
    label: '전자계약 방식',
    done: electronicMethod.value,
    target: 'cdoc-electronic',
  })
  items.push({
    key: 'final',
    label: '최종 동의',
    done: finalAgree.value,
    target: 'cdoc-final',
  })
  return items
})

watch(ready, (value) => emit('update:ready', value), { immediate: true })
watch(checkProgress, (items) => emit('update:checks', items), { immediate: true, deep: true })

function shortTitle(title: string) {
  return title.replace(/^별첨\s*/, '별첨 ').slice(0, 28)
}

function scrollTo(id: string) {
  document.getElementById(`cdoc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function downloadTxt() {
  downloadLegalText({
    code: 'seller_supply_contract',
    version: props.version || 'contract',
    body: props.body,
    title: props.title,
  })
}

async function downloadPdf() {
  if (pdfBusy.value) return
  pdfBusy.value = true
  try {
    if (props.downloadStoredPdf) {
      await props.downloadStoredPdf()
      return
    }
    await downloadContractPdf({
      title: props.title,
      version: props.version,
      effective: props.effective,
      body: props.body,
      party: props.party,
      signature: props.signature,
      signedAt: props.signedAt,
      signerName: props.signerName,
      electronicMethod: electronicMethod.value || props.mode === 'view',
    })
  } catch (error) {
    console.error(error)
    window.alert(
      error instanceof Error
        ? error.message
        : '계약서 PDF를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    )
  } finally {
    pdfBusy.value = false
  }
}

defineExpose({ ready, downloadPdf })
</script>

<style lang="scss" src="./ContractDocumentView.scss"></style>
